/**
 * GET /api/park/allocate — fill-first + opcjonalne ?with= (§18, §6.1).
 */
import {
  ParkAllocateResponseSchema,
  type ParkAllocateResponse,
} from '@pieskowo/shared';
import { SaveLocal } from '../core/SaveLocal.js';

export type AllocateResult =
  | { ok: true; data: ParkAllocateResponse }
  | { ok: false; code: string; status: number; offline?: boolean };

function apiBase(): string {
  try {
    const v = import.meta.env?.VITE_API_URL;
    if (typeof v === 'string' && v.length > 0) return v.replace(/\/$/, '');
  } catch {
    /* vitest / node */
  }
  return '';
}

/**
 * Alokuj pokój parku. Wymaga Bearer token (requireAuth na serwerze).
 * @param withPlayerId opcjonalne routing do pokoju znajomego (§6.1 ?with=)
 */
export async function allocateParkRoom(opts?: {
  withPlayerId?: string;
  fetchImpl?: typeof fetch;
}): Promise<AllocateResult> {
  const fetchFn = opts?.fetchImpl ?? fetch;
  const token = SaveLocal.getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  let path = `${apiBase()}/api/park/allocate`;
  if (opts?.withPlayerId) {
    path += `?with=${encodeURIComponent(opts.withPlayerId)}`;
  }

  try {
    const res = await fetchFn(path, { method: 'GET', headers });
    if (!res.ok) {
      let code = `HTTP_${res.status}`;
      try {
        const errJson = (await res.json()) as { error?: { code?: string } };
        if (errJson?.error?.code) code = errJson.error.code;
      } catch {
        /* ignore */
      }
      return { ok: false, code, status: res.status };
    }
    const json: unknown = await res.json();
    const parsed = ParkAllocateResponseSchema.safeParse(json);
    if (!parsed.success) {
      return { ok: false, code: 'VALIDATION', status: res.status };
    }
    return { ok: true, data: parsed.data };
  } catch {
    return { ok: false, code: 'OFFLINE', status: 0, offline: true };
  }
}
